import {
  SourceEntityMigrationType,
  type ISourceEntity,
  IExpectedSourceRecordSize,
  IPracticeMigration,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import { TypeGuard, WithRef, toTimestamp } from '@principle-theorem/shared';
import { flow, isNull, isNumber, isString, uniqBy } from 'lodash';
import { IBaseMigrationItemCode } from '../../../interfaces';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';
import {
  convertKeysToCamelCaseFn,
  convertNullToUndefinedFn,
} from '../../../source/source-helpers';
import { runQuery } from '../../../source/connection';

export const ADA_ITEM_RESOURCE_TYPE = 'adaItem';

export const ADA_ITEM_SOURCE_ENTITY: ISourceEntity = SourceEntity.init({
  metadata: {
    label: 'ADA Item List',
    description: `
      Exact does not enforce explicit service codes in their system, and they do not provide any table that defines what service codes users can use in the system. To combat this
      we run a query for distinct uses of service codes in the treatment table. This may return some odd results so we MUST complete the translation maps
      to be uploaded in the Custom Mappings sections before doing the Treatment Plans and Charts destination migrations.
    `,
    idPrefix: ADA_ITEM_RESOURCE_TYPE,
    migrationType: SourceEntityMigrationType.Automatic,
  },
});

export interface IExactADAItem extends IBaseMigrationItemCode {
  id: number;
}

export function isExactADAItemListItem(
  item: unknown,
  logError: boolean
): item is IExactADAItem {
  return TypeGuard.interface<IExactADAItem>(
    {
      id: isNumber,
      itemCode: isString,
      description: [isString, isNull],
    },
    logError
  )(item);
}

const ADA_ITEM_SOURCE_QUERY = `
SELECT
	DISTINCT(servicecode) AS item_code,
	servicedescription AS description,
	DENSE_RANK() OVER (results) AS id
FROM convtreatment
WHERE servicecode != ''
WINDOW results AS (ORDER BY servicecode)
ORDER BY id
`;

export type ExactADAItem = ISourceEntityTypes<IExactADAItem>;

export class ADAItemSourceEntity extends BaseSourceEntity<ExactADAItem> {
  sourceEntity = ADA_ITEM_SOURCE_ENTITY;
  entityResourceType = ADA_ITEM_RESOURCE_TYPE;
  sourceQuery = ADA_ITEM_SOURCE_QUERY;
  verifySourceFn = isExactADAItemListItem;

  override transformDataFn = flow([
    convertKeysToCamelCaseFn(),
    convertNullToUndefinedFn(),
    transformItemCodeResults,
  ]);

  override async getExpectedRecordSize(
    migration: WithRef<IPracticeMigration>
  ): Promise<IExpectedSourceRecordSize> {
    const response = await runQuery<IExactADAItem>(migration, this.sourceQuery);
    const codes = this.transformDataFn(response.rows) as IExactADAItem[];

    return {
      expectedSize: codes.length,
      expectedSizeCalculatedAt: toTimestamp(),
    };
  }

  translate(): Record<symbol, unknown> {
    return {};
  }

  getSourceRecordId(data: Pick<IExactADAItem, 'itemCode'>): string {
    return data.itemCode;
  }

  getSourceLabel(data: IExactADAItem): string {
    return `${data.itemCode}`;
  }
}

function transformItemCodeResults(results: IExactADAItem[]): IExactADAItem[] {
  return uniqBy(
    results.map((row) => ({
      ...row,
      itemCode: row.itemCode.replace(/\//g, ' '),
    })),
    'itemCode'
  );
}
